import { Button, ListboxItem } from "@heroui/react"
import { Plural, Trans, useLingui } from "@lingui/react/macro"
import { File, Filter } from "lucide-react"
import { useMemo, useState } from "react"

import { FileIcon } from "#/components/FileIcon"
import { count } from "#/toolbar/functions"
import { triggerStyle } from "#/toolbar/style"

import { FilterModal, type FilterModalProps } from "../components/FilterModal"
import type { Har } from "../har"

function reverseDNS(host: string) {
  return host.split(".").reverse().join(".")
}

const aliasMap: Record<string, string[]> = {
  CSS: ["text/css"],
  Favicon: ["image/vnd.microsoft.icon", "image/x-icon"],
  GIF: ["image/gif"],
  glTF: ["model/gltf-binary", "model/gltf+json"],
  HTML: ["text/html"],
  JavaScript: ["text/javascript", "application/javascript", "application/x-javascript"],
  JPEG: ["image/jpeg"],
  JSON: ["application/json", "text/json"],
  PDF: ["application/pdf"],
  PNG: ["image/png"],
  Protobuf: ["application/protobuf"],
  SVG: ["image/svg+xml"],
  Text: ["text/plain"],
  TTF: ["font/ttf"],
  Unknown: ["application/octet-stream", "x-unknown"],
  WebAssembly: ["application/wasm"],
  WOFF: ["application/font-woff", "font/woff"],
  WOFF2: ["font/woff2", "application/font-woff2"],
  XML: ["application/xml", "text/xml"],
}

const knownAlias = new Map(
  Object.entries(aliasMap).flatMap(([key, list]) => list.map(v => [v, key] as const))
)

const fallbackIcon = <File css="ml-0.5 opacity-80" size={16} />

export function MimeTypeIcon({ type }: { type: string }) {
  return <FileIcon className="w-4" fallback={fallbackIcon} mimeType={type} />
}

function MimeTypeEntry([key, count]: [string, number]) {
  const alias = knownAlias.get(key)
  // const isMostCommon = alias != null && aliasMap[alias][0] === key

  return (
    <ListboxItem key={key} startContent={<MimeTypeIcon type={key} />} textValue={key}>
      <div>
        <span>
          {alias ?? key}{" "}
          <span css="opacity-50">
            {wrap(
              [count > 1 && count.toString(), alias != null && key]
                .filter(Boolean)
                .join(" × ")
            )}
          </span>
        </span>
      </div>
    </ListboxItem>
  )
}

function wrap(text: string) {
  return text ? `(${text})` : null
}

interface MimeTypeModalProps
  extends Pick<
    FilterModalProps<[string, number]>,
    | "isOpen"
    | "onOpenChange"
    | "onIncludeChange"
    | "onExcludeChange"
    | "includeValue"
    | "excludeValue"
  > {
  har?: Har
}

function MimeTypeModal({ har, ...props }: MimeTypeModalProps) {
  const { t } = useLingui()
  const { onExcludeChange, excludeValue } = props

  const counts = useMemo(
    () =>
      count(
        har?.log.entries.filter(x => x.response.content.mimeType),
        e => e.response.content.mimeType!.split(";")[0]
      ),
    [har]
  )

  const allOptions = useMemo(
    () =>
      Array.from(counts).sort((a, b) => reverseDNS(a[0]).localeCompare(reverseDNS(b[0]))),
    [counts]
  )

  return (
    <FilterModal
      excludeLabel={t`Excluded mime types`}
      filter={([key], query) => key.toLowerCase().includes(query.toLowerCase())}
      includeLabel={t`Included mime types`}
      items={allOptions}
      {...props}
      footerActions={
        <Button
          color="danger"
          css="border-1"
          variant="bordered"
          onPress={() => onExcludeChange(dataOnly.union(excludeValue))}
        >
          <Trans>Data Only</Trans>
        </Button>
      }
      header={
        <>
          <Filter size={17} />
          <Trans>Mime Type</Trans>
        </>
      }
    >
      {MimeTypeEntry}
    </FilterModal>
  )
}

export function MimeTypeButton({
  har,
  includeValue,
  excludeValue,
  onExcludeChange,
  onIncludeChange,
}: Pick<
  MimeTypeModalProps,
  "har" | "includeValue" | "excludeValue" | "onExcludeChange" | "onIncludeChange"
>) {
  const [isOpen, onOpenChange] = useState(false)

  let label: React.ReactNode
  if (includeValue.size === 1) {
    const mimeType = includeValue.values().next().value!
    label = (
      <>
        <MimeTypeIcon type={mimeType} />
        {mimeType}
      </>
    )
  } else if (includeValue.size) {
    label = (
      <>
        {fallbackIcon}
        <Plural
          one="# type included"
          other="# types included"
          value={includeValue.size}
        />
      </>
    )
  } else if (excludeValue.size) {
    label = (
      <>
        {fallbackIcon}
        <Plural
          one="# type included"
          other="# types included"
          value={excludeValue.size}
        />
      </>
    )
  } else {
    label = (
      <span css="flex items-center gap-1.5 text-foreground-500">
        <File size={14} />
        <Trans>Filter by mime types…</Trans>
      </span>
    )
  }

  return (
    <>
      <Button
        className={triggerStyle}
        css="text-[14px]"
        size="sm"
        variant="flat"
        onPress={() => onOpenChange(true)}
      >
        {label}
      </Button>

      <MimeTypeModal
        {...{
          excludeValue,
          har,
          includeValue,
          isOpen,
          onExcludeChange,
          onIncludeChange,
          onOpenChange,
        }}
      />
    </>
  )
}

const dataOnly = new Set([
  "application/font-woff",
  "application/font-woff2",
  "application/javascript",
  "application/x-javascript",
  "font/woff",
  "image/gif",
  "image/jpeg",
  "image/svg+xml",
  "image/vnd.microsoft.icon",
  "text/css",
  "text/javascript",
])
